"use client";

import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Remodel() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const hours = [{
    day: "Monday – Friday",
    time: "8:00 AM – 6:00 PM"
  }, {
    day: "Saturday",
    time: "9:00 AM – 2:00 PM"
  }];
  const landmarks = [{
    label: "downtown",
    x: 90,
    y: 110,
    dotX: 165,
    dotY: 165
  }, {
    label: "lake mirror",
    x: 410,
    y: 130,
    dotX: 350,
    dotY: 180
  }, {
    label: "polk parkway",
    x: 80,
    y: 360,
    dotX: 175,
    dotY: 320
  }, {
    label: "florida southern",
    x: 410,
    y: 380,
    dotX: 340,
    dotY: 335
  }];
  return <section data-section-id="11886" id="lets-talk-remodeling" className="dark bg-background text-foreground relative overflow-hidden py-28 md:py-44 px-5 md:px-10">
      <div className="max-w-[1240px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-6 transition-all duration-[620ms] ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="font-mono text-primary text-2xl md:text-3xl mb-5" style={{
            transitionDelay: "80ms"
          }}>Bathroom Remodel Quotes</p>

            <h1 className="font-serif font-default text-foreground text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-10">Bathroom Remodel Quotes in Lakeland, FL</h1>

            <div className="space-y-9 mb-10">
              <div className="flex gap-5">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-11 h-11 rounded-lg border border-border flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" strokeWidth={2} />
                  </div>
                </div>
                <div>
                  <p className="font-default font-semibold text-foreground/70 text-xs tracking-widest uppercase mb-2">
                    Service Area
                  </p>
                  <p className="font-serif font-default text-foreground text-2xl md:text-3xl leading-snug">Serving Lakeland, FL and Nearby Polk County Areas</p>
                </div>
              </div>

              <div className="flex gap-5">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-11 h-11 rounded-lg border border-border flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" strokeWidth={2} />
                  </div>
                </div>
                <div>
                  <p className="font-default font-semibold text-foreground/70 text-xs tracking-widest uppercase mb-2">
                    Availability
                  </p>
                  <div className="space-y-1">
                    {hours.map((h, i) => <p key={i} data-index={i} className="font-serif font-default text-foreground text-2xl md:text-3xl leading-snug">
                        {h.day}
                        <span className="text-foreground/70 text-xl md:text-2xl">
                          {" "}
                          · {h.time}
                        </span>
                      </p>)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-7 py-6 text-base font-semibold shadow-md">
                <Link to="/Contact">
                  <Navigation className="w-4 h-4" strokeWidth={2.2} />
                  Request Quote
                </Link>
              </Button>

              <Button asChild variant="outline" className="border-2 border-foreground text-foreground bg-transparent hover:bg-foreground hover:text-background rounded-lg px-7 py-6 text-base font-semibold transition-colors">
                <Link to="/contact/#quote">Get a free quote</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={mapRef} className={`lg:col-span-6 relative transition-all duration-[620ms] ease-out ${mapVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="relative w-full max-w-[560px] mx-auto h-[400px] md:h-[520px]">
              <svg viewBox="0 0 500 500" className="w-full h-full" fill="none" aria-hidden="true">
                <defs>
                  <style>{`
                    .map-path {
                      stroke-dasharray: 600;
                      stroke-dashoffset: ${mapVisible ? 0 : 600};
                      transition: stroke-dashoffset 1200ms ease-out;
                    }
                  `}</style>
                </defs>

                {}
                <path d="M 20 250 Q 100 245, 180 252 T 360 248 T 485 254" stroke="var(--foreground)" strokeWidth="2.5" strokeLinecap="round" className="map-path" style={{
                transitionDelay: "100ms"
              }} />

                {}
                <path d="M 250 30 Q 245 110, 252 190 T 248 360 T 254 480" stroke="var(--foreground)" strokeWidth="2.5" strokeLinecap="round" className="map-path" style={{
                transitionDelay: "300ms"
              }} />

                {}
                <path d="M 60 80 Q 120 95, 180 110 Q 220 120, 250 130" stroke="var(--foreground)" strokeWidth="2" strokeLinecap="round" className="map-path" style={{
                transitionDelay: "500ms",
                opacity: 0.7
              }} />

                {}
                <path d="M 260 370 Q 320 380, 380 395 Q 420 405, 460 415" stroke="var(--foreground)" strokeWidth="2" strokeLinecap="round" className="map-path" style={{
                transitionDelay: "700ms",
                opacity: 0.7
              }} />

                {}
                <path d="M 380 60 Q 400 120, 420 180" stroke="var(--foreground)" strokeWidth="1.8" strokeLinecap="round" className="map-path" style={{
                transitionDelay: "900ms",
                opacity: 0.5
              }} />

                {}
                <g style={{
                opacity: mapVisible ? 1 : 0,
                transition: "opacity 600ms ease-out 1100ms"
              }}>
                  <circle cx="155" cy="155" r="14" stroke="var(--foreground)" strokeWidth="1.8" opacity="0.6" />
                  <circle cx="175" cy="165" r="10" stroke="var(--foreground)" strokeWidth="1.8" opacity="0.6" />
                  <circle cx="165" cy="180" r="11" stroke="var(--foreground)" strokeWidth="1.8" opacity="0.6" />
                </g>

                {}
                <g style={{
                opacity: mapVisible ? 1 : 0,
                transition: "opacity 600ms ease-out 1200ms"
              }}>
                  <path d="M 335 170 L 335 195 L 370 195 L 370 170 L 352 158 Z" stroke="var(--foreground)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
                  <path d="M 348 195 L 348 182 L 358 182 L 358 195" stroke="var(--foreground)" strokeWidth="1.5" opacity="0.6" />
                </g>

                {}
                <g style={{
                opacity: mapVisible ? 1 : 0,
                transition: "opacity 600ms ease-out 1300ms"
              }}>
                  <circle cx="170" cy="315" r="4" fill="var(--foreground)" opacity="0.7" />
                  <circle cx="180" cy="320" r="3" fill="var(--foreground)" opacity="0.5" />
                </g>

                {}
                <g style={{
                opacity: mapVisible ? 1 : 0,
                transition: "opacity 600ms ease-out 1400ms"
              }}>
                  <path d="M 325 320 L 325 345 L 360 345 L 360 320 L 342 308 Z" stroke="var(--foreground)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
                </g>

                {}
                <g stroke="var(--primary)" strokeWidth="1.5" strokeDasharray="2 4" strokeLinecap="round" style={{
                opacity: mapVisible ? 0.7 : 0,
                transition: "opacity 500ms ease-out 1500ms"
              }}>
                  <path d="M 115 125 L 160 160" fill="none" />
                  <path d="M 395 140 L 355 175" fill="none" />
                  <path d="M 110 350 L 165 325" fill="none" />
                  <path d="M 410 370 L 355 340" fill="none" />
                </g>

                {}
                <g style={{
                opacity: mapVisible ? 1 : 0,
                transform: mapVisible ? "scale(1)" : "scale(0.5)",
                transformOrigin: "250px 250px",
                transition: "opacity 500ms ease-out 1700ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) 1700ms"
              }}>
                  <circle cx="250" cy="250" r="22" fill="var(--primary)" />
                  <circle cx="250" cy="250" r="28" stroke="var(--primary)" strokeWidth="1.5" fill="none" opacity="0.4" />
                  {}
                  <path d="M 250 238 Q 244 245, 244 252 Q 244 260, 250 262 Q 256 260, 256 252 Q 256 245, 250 238 Z" fill="var(--primary-foreground)" />
                </g>
              </svg>

              {}
              {landmarks.map((lm, i) => <div key={i} data-index={i} className="absolute font-mono text-foreground text-xl md:text-2xl whitespace-nowrap pointer-events-none" style={{
              left: `${lm.x / 500 * 100}%`,
              top: `${lm.y / 500 * 100}%`,
              opacity: mapVisible ? 1 : 0,
              transform: mapVisible ? "translateY(0)" : "translateY(8px)",
              transition: `opacity 500ms ease-out ${1600 + i * 150}ms, transform 500ms ease-out ${1600 + i * 150}ms`
            }}>
                  {lm.label}
                </div>)}

              {}
              <div className="absolute font-mono text-primary text-2xl md:text-3xl whitespace-nowrap pointer-events-none" style={{
              left: "54%",
              top: "48%",
              opacity: mapVisible ? 1 : 0,
              transition: "opacity 500ms ease-out 2000ms"
            }}>
                Lakeland
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}